// Marketing compose modal — two-pane editor (left) / live preview
// (right). Mirrors the HQ Updates ComposeModal but scoped to the
// franchisee's own Territory+ clients and capped at 5 recipients
// per send. Now supports:
//   • Multi-panel sections — each panel = {intro, image, link}
//     joined in the email by the brand-yellow horizontal divider.
//   • Rich-text intro on each panel (bold + centre via tiny toolbar).
//   • "Save draft" — persists the in-progress campaign so the user
//     can come back and finish later. Loaded drafts re-open here
//     pre-filled with their stored panels/recipients.
import { useEffect, useState, useMemo, useCallback, useRef } from "react";
import {
  X, Send, Loader2, Search, CheckCircle2, AlertCircle, Image as ImageIcon,
  Trash2, Link as LinkIcon, Calendar, Megaphone, RefreshCw, Plus, FileText, Mail,
  Bold, Italic, Underline as UnderlineIcon, AlignLeft, AlignCenter, AlignRight,
  User,
} from "lucide-react";
import api from "@/lib/api";
import MarketingImageCropper from "@/components/portal/marketing/MarketingImageCropper";
import ProjectPoolPickerModal from "@/components/portal/marketing/ProjectPoolPickerModal";
import MarketingIntroEditor, { HEADING_FONTS } from "@/components/portal/marketing/MarketingIntroEditor";

const MAX_RECIPIENTS = 5;
const MAX_PANELS = 8;

// Web-safe font whitelist is imported from MarketingIntroEditor —
// single source of truth mirrored server-side in
// ``_SAFE_HEADING_FONTS``. See that module for the full stack table.

// Default heading styles — used for hydration when a legacy campaign
// or template doesn't carry a style dict. Kept intentionally close to
// the historical hardcoded values so existing docs render identically.
const DEFAULT_DISPLAY_HEADING_STYLE = Object.freeze({
  font_family: "Helvetica", font_size: 24, font_weight: 800, color: "#1a1a1a",
  bold: true, italic: false, underline: false, align: "center",
});
const DEFAULT_SECTION_HEADER_STYLE = Object.freeze({
  font_family: "Helvetica", font_size: 20, font_weight: 800, color: "#1a1a1a",
  bold: true, italic: false, underline: false, align: "left",
});
// Body-text defaults (Intro Text + Section Text). Mirrors the backend
// ``_BODY_TEXT_DEFAULTS`` in portal_marketing_routes.py so an
// unauthored block renders identically before AND after this feature
// shipped — no visual break for legacy campaigns/templates.
const DEFAULT_BODY_TEXT_STYLE = Object.freeze({
  font_family: "Helvetica", font_size: 15, font_weight: 400, color: "#1a1a1a",
  bold: false, italic: false, underline: false, align: "left",
});

const emptyPanel = () => ({
  header: "",
  header_style: { ...DEFAULT_SECTION_HEADER_STYLE },
  text_html: "",
  // ``text_style: null`` = inherit the campaign-level intro_style.
  // Every new section starts inheriting so authors only need to edit
  // the intro row once. Setting this to a dict flips the section
  // into an explicit override.
  text_style: null,
  image_url: "",
  image_key: "",
  layout: "image-top",  // "image-top" | "image-left" | "image-right"
  link_type: "url",     // "url" | "email"
  link_url: "",
  link_label: "Find out more",
  email_subject: "I'd like more information please",
});

// Marketing+ v2 — header selector defaults. Standard = the Creative
// Mojo logo wired to the franchisee's chosen destination. Custom =
// a per-campaign banner image, capped at 600×320 server-side.
const HEADER_STANDARD = "standard";
const HEADER_CUSTOM = "custom";

// Used to pre-fill the top-level intro field of a brand-new campaign
// with the franchisee's personalised "Hi {{first_name}}," greeting so
// they can just keep typing their copy underneath.
const DEFAULT_INTRO_HTML =
  '<div>Hi <strong>{{first_name}}</strong>,</div><div><br/></div>';

const DEFAULT_BG = "#f7f7f4";

export default function MarketingComposeModal({
  open, access, draft, preselectClientId, onClose, onSent, onDraftSaved,
  // Template-mode props (HQ Marketing+ Templates). When mode==="template"
  // the composer is used as the HQ template editor: same editor + live
  // preview, but recipients/bookings/footer/send are hidden and the
  // footer shows Save / Publish / Unpublish / Archive actions instead.
  // Franchisee behaviour is unaffected — mode defaults to "campaign".
  mode = "campaign", template = null, onTemplateSaved,
}) {
  const isTemplate = mode === "template";
  const previewEndpoint = isTemplate
    ? "/admin/marketing/templates/preview-html"
    : "/portal/marketing/preview-html";
  const uploadEndpoint = isTemplate
    ? "/admin/marketing/templates/upload-header-image"
    : "/portal/marketing/upload-image";
  // Template-mode state (unused in campaign mode).
  const [templateName, setTemplateName] = useState("");
  const [templateDescription, setTemplateDescription] = useState("");
  const [templateStatus, setTemplateStatus] = useState("draft");
  const [templateId, setTemplateId] = useState(null);
  const [templateSaving, setTemplateSaving] = useState(false);
  const [draftId, setDraftId] = useState(null);
  const [title, setTitle] = useState("");
  // Visible in-email heading — separate from ``title`` (which is the
  // mailbox subject line). Backwards-compatible: legacy docs without
  // this field default to empty and render nothing; the backend
  // renderer additionally echoes ``title`` for pre-split docs so
  // existing campaigns stay visually stable until edited.
  const [displayHeading, setDisplayHeading] = useState("");
  const [displayHeadingStyle, setDisplayHeadingStyle] = useState({ ...DEFAULT_DISPLAY_HEADING_STYLE });
  // Ref bridging the single-toolbar row (HeadingStyleControls) to the
  // Intro Text editor for its Link + Insert Name actions. The editor
  // itself no longer carries a toolbar.
  const introEditorRef = useRef(null);
  // Intro Text block-level typography — same style dict shape as the
  // heading controls. Missing on legacy campaigns → renders with the
  // historical body defaults on the server. Local default keeps the
  // control usable on new campaigns/templates.
  const [introStyle, setIntroStyle] = useState({ ...DEFAULT_BODY_TEXT_STYLE });
  // Top-level intro lives OUTSIDE the sections so it always sits above
  // section 1. The franchisee personalises it once and every section
  // can be reordered/added/removed without losing the greeting.
  const [introHtml, setIntroHtml] = useState(DEFAULT_INTRO_HTML);
  const [backgroundColor, setBackgroundColor] = useState(DEFAULT_BG);
  // Optional inner-body-panel background — paints the content card
  // below the header graphic. Empty string = keep the historical
  // white so legacy campaigns/templates render identically without
  // any data migration.
  const [bodyBackgroundColor, setBodyBackgroundColor] = useState("");
  const [panels, setPanels] = useState([emptyPanel()]);
  // Marketing+ v2 header selector.
  const [headerType, setHeaderType] = useState(HEADER_STANDARD);
  const [customHeaderImageUrl, setCustomHeaderImageUrl] = useState("");
  const [customHeaderImageKey, setCustomHeaderImageKey] = useState("");
  const [customHeaderUploading, setCustomHeaderUploading] = useState(false);
  const [includeBookings, setIncludeBookings] = useState(false);
  // Per-send footer-contact checkboxes. Persisted with drafts so the
  // franchisee's choices stick on every Save → reload cycle.
  const [footerShowPhone, setFooterShowPhone] = useState(false);
  const [footerShowEmail, setFooterShowEmail] = useState(true);
  const [footerShowFacebook, setFooterShowFacebook] = useState(false);
  const [footerShowInstagram, setFooterShowInstagram] = useState(false);
  const [footerShowCustom, setFooterShowCustom] = useState(false);

  const [recipients, setRecipients] = useState([]);
  const [recipientSearch, setRecipientSearch] = useState("");
  const [selectedKeys, setSelectedKeys] = useState(new Set());
  const [recipientsLoading, setRecipientsLoading] = useState(false);

  const [previewHtml, setPreviewHtml] = useState("");
  const [cropOpen, setCropOpen] = useState(false);
  const [cropPanelIdx, setCropPanelIdx] = useState(null);
  const [rawImageFile, setRawImageFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  // Project Pool picker — remembers which section is picking so
  // the returned URL lands on the right panel. ``null`` = closed.
  const [poolPickerIdx, setPoolPickerIdx] = useState(null);
  const [sending, setSending] = useState(false);
  const [savingDraft, setSavingDraft] = useState(false);
  const [testing, setTesting] = useState(false);
  const [error, setError] = useState("");
  const [info, setInfo] = useState("");

  // Reset / hydrate whenever the modal opens or a different draft is loaded.
  useEffect(() => {
    if (!open) return;
    if (isTemplate) {
      // Hydrate from the passed-in template doc (or a blank template
      // for the "New Template" path). Uses the same schema the
      // franchisee composer uses — the template document is essentially
      // a stored campaign body without recipient/footer state.
      const t = template || {};
      setTemplateId(t.id || null);
      setTemplateName(t.name || "");
      setTemplateDescription(t.description || "");
      setTemplateStatus(t.status || "draft");
      setDraftId(null);
      setTitle(t.subject_line || "");
      setDisplayHeading(t.display_heading || "");
      setDisplayHeadingStyle({ ...DEFAULT_DISPLAY_HEADING_STYLE, ...(t.display_heading_style || {}) });
      setIntroStyle({ ...DEFAULT_BODY_TEXT_STYLE, ...(t.intro_style || {}) });
      setIntroHtml(t.intro_html || DEFAULT_INTRO_HTML);
      setBackgroundColor(t.background_color || DEFAULT_BG);
      setBodyBackgroundColor(t.body_background_color || "");
      setPanels(Array.isArray(t.panels) && t.panels.length
        ? t.panels.map((p) => ({
            ...emptyPanel(), ...p,
            text_html: p.text_html || p.intro || "",
            header_style: { ...DEFAULT_SECTION_HEADER_STYLE, ...(p.header_style || {}) },
            // ``null`` = inherit intro_style. A dict means the author
            // explicitly overrode this section — preserve it verbatim.
            text_style: p.text_style && typeof p.text_style === "object" ? p.text_style : null,
          }))
        : [emptyPanel()]);
      setIncludeBookings(false);
      setHeaderType(t.header_type || HEADER_STANDARD);
      setCustomHeaderImageUrl(t.custom_header_image_url || "");
      setCustomHeaderImageKey(t.custom_header_image_key || "");
      setFooterShowPhone(false); setFooterShowEmail(false);
      setFooterShowFacebook(false); setFooterShowInstagram(false); setFooterShowCustom(false);
      setRecipients([]); setRecipientSearch(""); setSelectedKeys(new Set());
      setRecipientsLoading(false);
      setPreviewHtml(""); setError(""); setInfo("");
      return;
    }
    if (draft) {
      setDraftId(draft.id);
      setTitle(draft.title || "");
      setDisplayHeading(draft.display_heading || "");
      setDisplayHeadingStyle({ ...DEFAULT_DISPLAY_HEADING_STYLE, ...(draft.display_heading_style || {}) });
      setIntroStyle({ ...DEFAULT_BODY_TEXT_STYLE, ...(draft.intro_style || {}) });
      // Drafts saved under the new shape carry intro_html separately;
      // older drafts may have intro/caption baked into panel 0 — leave
      // them as-is (per spec, no migration) but read whichever exists.
      setIntroHtml(draft.intro_html || "");
      setBackgroundColor(draft.background_color || DEFAULT_BG);
      setBodyBackgroundColor(draft.body_background_color || "");
      const dp = Array.isArray(draft.panels) && draft.panels.length
        ? draft.panels.map((p) => ({
            ...emptyPanel(),
            ...p,
            // Migrate legacy `intro` into `text_html` on the fly so
            // reopened older drafts edit correctly.
            text_html: p.text_html || p.intro || "",
            header_style: { ...DEFAULT_SECTION_HEADER_STYLE, ...(p.header_style || {}) },
            text_style: p.text_style && typeof p.text_style === "object" ? p.text_style : null,
          }))
        : [{
            ...emptyPanel(),
            text_html: draft.intro || "",
            image_url: draft.image_url || "",
            image_key: draft.image_key || "",
            link_url: draft.link_url || "",
            link_label: draft.link_label || "Find out more",
          }];
      setPanels(dp);
      setIncludeBookings(!!draft.include_bookings_link);
      setHeaderType(draft.header_type || HEADER_STANDARD);
      setCustomHeaderImageUrl(draft.custom_header_image_url || "");
      setCustomHeaderImageKey(draft.custom_header_image_key || "");
      setFooterShowPhone(!!draft.footer_show_phone);
      setFooterShowEmail(draft.footer_show_email !== false);
      setFooterShowFacebook(!!draft.footer_show_facebook);
      setFooterShowInstagram(!!draft.footer_show_instagram);
      setFooterShowCustom(!!draft.footer_show_custom);
    } else {
      setDraftId(null);
      setTitle("");
      setDisplayHeading("");
      setDisplayHeadingStyle({ ...DEFAULT_DISPLAY_HEADING_STYLE });
      setIntroStyle({ ...DEFAULT_BODY_TEXT_STYLE });
      setIntroHtml(DEFAULT_INTRO_HTML);
      setBackgroundColor(DEFAULT_BG);
      setBodyBackgroundColor("");
      setPanels([emptyPanel()]);
      setIncludeBookings(false);
      setHeaderType(HEADER_STANDARD);
      setCustomHeaderImageUrl("");
      setCustomHeaderImageKey("");
      // Pre-tick footer toggles from the franchisee's most recently
      // sent campaign so they don't keep re-ticking the same boxes.
      // Falls back to historical defaults (Email only) for first-time
      // senders. Boxes only stay ticked if the matching URL/field is
      // actually configured — handled by the JSX's checked condition.
      const last = access?.last_footer_selection || null;
      if (last) {
        setFooterShowPhone(!!last.phone);
        setFooterShowEmail(last.email !== false);
        setFooterShowFacebook(!!last.facebook);
        setFooterShowInstagram(!!last.instagram);
        setFooterShowCustom(!!last.custom);
      } else {
        setFooterShowPhone(false);
        setFooterShowEmail(true);
        setFooterShowFacebook(false);
        setFooterShowInstagram(false);
        setFooterShowCustom(false);
      }
    }
    setRecipientSearch(""); setSelectedKeys(new Set());
    setPreviewHtml(""); setError(""); setInfo("");
    if (isTemplate) {
      // Template mode never sends to franchisee recipients — the
      // picker is hidden and no fetch is needed.
      setRecipients([]);
      setRecipientsLoading(false);
      return;
    }
    setRecipientsLoading(true);
    api.get("/portal/marketing/recipients")
      .then(({ data }) => {
        const items = data.items || [];
        setRecipients(items);
        // Deep-link from My Territory+: pre-tick every recipient row
        // belonging to the passed-in client_id, capped at MAX_RECIPIENTS
        // so we never blow past the per-send limit.
        if (preselectClientId) {
          const keys = new Set();
          for (const r of items) {
            if (r.client_id === preselectClientId) {
              keys.add(`${r.client_id}:${r.contact_index}`);
              if (keys.size >= MAX_RECIPIENTS) break;
            }
          }
          if (keys.size) setSelectedKeys(keys);
        }
      })
      .catch(() => setRecipients([]))
      .finally(() => setRecipientsLoading(false));
  }, [open, draft, preselectClientId]);

  // Live preview render (debounced).
  useEffect(() => {
    if (!open) return;
    const t = setTimeout(async () => {
      try {
        const { data } = await api.post(previewEndpoint, {
          title,
          display_heading: displayHeading,
          display_heading_style: displayHeadingStyle,
          intro_style: introStyle,
          panels,
          intro_html: introHtml,
          background_color: backgroundColor,
          body_background_color: bodyBackgroundColor,
          header_type: headerType,
          custom_header_image_url: customHeaderImageUrl,
          custom_header_image_key: customHeaderImageKey,
          bookings_url: includeBookings && access?.bookings_enabled
            ? `${window.location.origin}/portal/bookings` : "",
          sample_first_name: "Sandra",
          footer_show_phone: footerShowPhone,
          footer_show_email: footerShowEmail,
          footer_show_facebook: footerShowFacebook,
          footer_show_instagram: footerShowInstagram,
          footer_show_custom: footerShowCustom,
        });
        setPreviewHtml(data.html || "");
      } catch { /* swallow — preview is best-effort */ }
    }, 300);
    return () => clearTimeout(t);
  }, [open, title, displayHeading, displayHeadingStyle, introStyle, panels, introHtml, backgroundColor, bodyBackgroundColor, headerType, customHeaderImageUrl,
      customHeaderImageKey, includeBookings, access?.bookings_enabled,
      footerShowPhone, footerShowEmail, footerShowFacebook, footerShowInstagram, footerShowCustom]);

  const filteredRecipients = useMemo(() => {
    const needle = recipientSearch.trim().toLowerCase();
    if (!needle) return recipients;
    return recipients.filter((r) =>
      (r.name || "").toLowerCase().includes(needle) ||
      (r.organisation || "").toLowerCase().includes(needle) ||
      (r.email || "").toLowerCase().includes(needle) ||
      (r.role || "").toLowerCase().includes(needle)
    );
  }, [recipients, recipientSearch]);

  const toggleRecipient = (r) => {
    const key = `${r.client_id}:${r.contact_index}`;
    setSelectedKeys((s) => {
      const n = new Set(s);
      if (n.has(key)) {
        n.delete(key);
      } else {
        if (n.size >= MAX_RECIPIENTS) {
          setError(`Maximum ${MAX_RECIPIENTS} recipients per send.`);
          setTimeout(() => setError(""), 2000);
          return s;
        }
        n.add(key);
      }
      return n;
    });
  };

  const selectedRecipients = useMemo(
    () => recipients.filter((r) => selectedKeys.has(`${r.client_id}:${r.contact_index}`)),
    [recipients, selectedKeys],
  );

  // --- panels: update / add / remove ---
  const updatePanel = (idx, patch) =>
    setPanels((arr) => arr.map((p, i) => (i === idx ? { ...p, ...patch } : p)));
  const addPanel = () => setPanels((arr) => arr.length >= MAX_PANELS ? arr : [...arr, emptyPanel()]);
  const removePanel = (idx) =>
    setPanels((arr) => (arr.length <= 1 ? arr : arr.filter((_, i) => i !== idx)));

  // --- image crop pipeline ---
  const onPickImage = (idx, e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setCropPanelIdx(idx);
    setRawImageFile(f);
    setCropOpen(true);
    e.target.value = "";
  };
  const onCropDone = async (croppedBlob) => {
    setCropOpen(false);
    const idx = cropPanelIdx;
    if (!croppedBlob || idx === null) return;
    setUploading(true); setError("");
    try {
      const fd = new FormData();
      fd.append("file", croppedBlob, "marketing.jpg");
      const { data } = await api.post(uploadEndpoint, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      updatePanel(idx, { image_url: data.image_url, image_key: data.key });
    } catch (e) {
      setError(e?.response?.data?.detail || "Image upload failed");
    } finally { setUploading(false); }
  };

  // --- payload builders ---
  const baseBody = () => ({
    frontend_origin: typeof window !== "undefined" ? window.location.origin : "",
    title: title.trim(),
    display_heading: displayHeading.trim(),
    display_heading_style: displayHeadingStyle,
    intro_style: introStyle,
    intro_html: introHtml,
    background_color: backgroundColor,
    body_background_color: bodyBackgroundColor,
    header_type: headerType,
    custom_header_image_url: customHeaderImageUrl,
    custom_header_image_key: customHeaderImageKey,
    panels: panels.map((p) => ({
      header: p.header || "",
      header_style: p.header_style || DEFAULT_SECTION_HEADER_STYLE,
      text_html: p.text_html || "",
      text_style: p.text_style || null,
      image_url: p.image_url || "",
      image_key: p.image_key || "",
      layout: p.layout || "image-top",
      link_type: p.link_type || "url",
      link_url: p.link_url || "",
      link_label: p.link_label || (p.link_type === "email" ? "Email me" : "Find out more"),
      email_subject: p.email_subject || "",
    })),
    include_bookings_link: includeBookings && access?.bookings_enabled,
    footer_show_phone: footerShowPhone,
    footer_show_email: footerShowEmail,
    footer_show_facebook: footerShowFacebook,
    footer_show_instagram: footerShowInstagram,
    footer_show_custom: footerShowCustom,
  });
  const sendBody = () => ({
    ...baseBody(),
    draft_id: draftId || undefined,
    recipients: selectedRecipients.map((r) => ({
      client_id: r.client_id, contact_index: r.contact_index,
    })),
  });

  const saveDraft = async () => {
    setSavingDraft(true); setError(""); setInfo("");
    try {
      const { data } = await api.post("/portal/marketing/campaigns/draft", {
        ...baseBody(),
        id: draftId || undefined,
      });
      setDraftId(data.id);
      setInfo("Draft saved — you can come back and finish later.");
      onDraftSaved?.();
      return data.id;
    } catch (e) {
      setError(e?.response?.data?.detail || "Couldn't save draft.");
      return null;
    } finally { setSavingDraft(false); }
  };

  // Closing the modal must NEVER silently drop unsaved work. We
  // auto-save the in-progress campaign as a draft when there's
  // anything worth keeping, then close. The backdrop click is wired
  // to do nothing (per-user spec); the X button + Done button are
  // the only exit paths.
  const handleClose = useCallback(async () => {
    const worthSaving = (title || "").trim() || (introHtml || "").trim() || panels.some(
      (p) => (p.header || "").trim() || (p.text_html || "").trim() ||
             (p.image_url || "").trim() || (p.link_url || "").trim() ||
             (p.link_type === "email" && (p.email_subject || "").trim())
    );
    if (worthSaving && !sending) {
      try {
        await api.post("/portal/marketing/campaigns/draft", {
          ...baseBody(),
          id: draftId || undefined,
        });
        onDraftSaved?.();
      } catch { /* swallow — closing should not stall on a save error */ }
    }
    onClose?.();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [title, displayHeading, displayHeadingStyle, introStyle, introHtml, backgroundColor, bodyBackgroundColor, panels, sending, draftId, includeBookings, footerShowPhone, footerShowEmail, footerShowFacebook, footerShowInstagram, footerShowCustom]);

  const send = async () => {
    if (selectedRecipients.length === 0) {
      setError("Pick at least one recipient.");
      return;
    }
    const ok = window.confirm(
      `Send "${title.trim() || "(no subject)"}" to ${selectedRecipients.length} recipient${selectedRecipients.length === 1 ? "" : "s"}?`
    );
    if (!ok) return;
    setSending(true); setError(""); setInfo("");
    try {
      const { data } = await api.post("/portal/marketing/campaigns", sendBody());
      setInfo(`Sent to ${data.succeeded} recipient(s).`);
      onSent?.();
    } catch (e) {
      setError(e?.response?.data?.detail || "Send failed.");
    } finally { setSending(false); }
  };

  const sendTest = async () => {
    setTesting(true); setError(""); setInfo("");
    try {
      const { data } = await api.post("/portal/marketing/test-send", {
        ...baseBody(),
        sample_first_name: "Sandra",
      });
      setInfo(`Test email sent to ${data.to}.`);
    } catch (e) {
      setError(e?.response?.data?.detail || "Test send failed.");
    } finally { setTesting(false); }
  };

  const hasAnyContent = (introHtml || "").trim() || panels.some(
    (p) => (p.header || "").trim() || (p.text_html || "").trim() ||
           (p.image_url || "").trim() || (p.link_url || "").trim() ||
           (p.link_type === "email" && (p.email_subject || "").trim())
  );
  const canSend = title.trim() && hasAnyContent && selectedRecipients.length > 0 && !sending;
  const canSaveDraft = (title.trim() || hasAnyContent) && !savingDraft;

  // Template-mode actions (mode==="template" only). Persist via the
  // existing /admin/marketing/templates API — no new endpoints.
  const templateBody = () => ({
    name: templateName.trim(),
    description: templateDescription.trim(),
    subject_line: title.trim(),
    display_heading: displayHeading.trim(),
    display_heading_style: displayHeadingStyle,
    intro_style: introStyle,
    intro_html: introHtml,
    background_color: backgroundColor,
    body_background_color: bodyBackgroundColor,
    header_type: headerType,
    custom_header_image_url: customHeaderImageUrl,
    custom_header_image_key: customHeaderImageKey,
    panels: panels.map((p) => ({
      header: p.header || "",
      header_style: p.header_style || DEFAULT_SECTION_HEADER_STYLE,
      text_html: p.text_html || "",
      text_style: p.text_style || null,
      image_url: p.image_url || "",
      image_key: p.image_key || "",
      layout: p.layout || "image-top",
      link_type: p.link_type || "url",
      link_url: p.link_url || "",
      link_label: p.link_label || (p.link_type === "email" ? "Email me" : "Find out more"),
      email_subject: p.email_subject || "",
    })),
  });
  const saveTemplate = async () => {
    if (!templateName.trim()) { setError("Template name is required."); return null; }
    setTemplateSaving(true); setError(""); setInfo("");
    try {
      let saved;
      if (templateId) {
        const { data } = await api.patch(`/admin/marketing/templates/${templateId}`, templateBody());
        // API returns { ok: true, template: {...} } — unwrap defensively.
        saved = data?.template || data;
      } else {
        const { data } = await api.post("/admin/marketing/templates", templateBody());
        saved = data?.template || data;
        setTemplateId(saved?.id || null);
      }
      setTemplateStatus(saved?.status || templateStatus);
      setInfo("Template saved.");
      onTemplateSaved?.(saved);
      return saved;
    } catch (e) {
      setError(e?.response?.data?.detail || "Save failed.");
      return null;
    } finally { setTemplateSaving(false); }
  };
  const templateStatusChange = async (newStatus, confirmMsg) => {
    // Save first so we never publish a mid-edit template.
    const saved = await saveTemplate();
    if (!saved?.id) return;
    if (confirmMsg && !window.confirm(confirmMsg)) return;
    const path = newStatus === "published" ? "publish"
      : newStatus === "draft" ? "unpublish"
      : "archive";
    try {
      const { data } = await api.post(`/admin/marketing/templates/${saved.id}/${path}`);
      const doc = data?.template || data;
      setTemplateStatus(doc?.status || newStatus);
      setInfo(`Template ${path === "publish" ? "published" : path === "unpublish" ? "unpublished" : "archived"}.`);
      onTemplateSaved?.(doc);
    } catch (e) {
      setError(e?.response?.data?.detail || `${path} failed.`);
    }
  };

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-50 bg-stone-950/40 backdrop-blur-sm flex items-stretch justify-center p-4 overflow-y-auto"
      data-testid="marketing-compose-modal"
    >
      <div
        className="bg-white w-full max-w-[1400px] rounded-2xl border border-stone-200 shadow-2xl my-auto flex flex-col max-h-[95vh]"
      >
        <div className="px-6 py-4 border-b border-stone-200 flex items-start justify-between gap-4">
          <div>
            <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-stone-500 flex items-center gap-1.5">
              <Megaphone className="w-3 h-3" /> {isTemplate ? "Marketing+ · Head Office" : (draftId ? "Edit Draft" : "Send Campaign")}
            </div>
            <h2 className="font-display text-2xl font-black text-stone-950 mt-1">
              {isTemplate ? (templateId ? "Edit Template" : "New Template") : "Compose Campaign"}
            </h2>
          </div>
          <button
            onClick={handleClose}
            data-testid="marketing-compose-close"
            className="w-9 h-9 rounded-full border border-stone-300 hover:bg-stone-50 flex items-center justify-center"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Body — two-pane */}
        <div className="flex-1 overflow-y-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-0">
            {/* LEFT — editor */}
            <div className="px-6 py-6 space-y-10 border-r border-stone-200">
              {/* ============================================================
                   TEMPLATE / EMAIL INFO
                   ============================================================ */}
              <FormGroup title={isTemplate ? "Template & email info" : "Email info"}>
              {isTemplate && (
                <>
                  <Field label="Template name" required>
                    <input
                      value={templateName}
                      onChange={(e) => setTemplateName(e.target.value)}
                      data-testid="template-name"
                      className="w-full px-3 py-2.5 border border-stone-300 rounded-xl text-sm focus:outline-none focus:border-stone-950"
                      placeholder="e.g. Summer Sessions Launch"
                    />
                  </Field>
                  <Field label="Short description">
                    <input
                      value={templateDescription}
                      onChange={(e) => setTemplateDescription(e.target.value)}
                      data-testid="template-description"
                      className="w-full px-3 py-2.5 border border-stone-300 rounded-xl text-sm focus:outline-none focus:border-stone-950"
                      placeholder="Shown on the franchisee picker card"
                    />
                  </Field>
                </>
              )}
              <Field label={isTemplate ? "Default email subject line" : "Email subject line"} required>
                <input
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  data-testid="marketing-title"
                  className="w-full px-3 py-2.5 border border-stone-300 rounded-xl text-sm focus:outline-none focus:border-stone-950"
                  placeholder={isTemplate ? "e.g. Summer sessions are back!" : "e.g. New summer craft sessions"}
                />
                <div className="mt-1 text-[11px] text-stone-500 italic">
                  Plain text — this is the subject that appears in the recipient&rsquo;s inbox.
                </div>
              </Field>
              </FormGroup>

              {/* ============================================================
                   HEADING (display heading + intro text)
                   ============================================================ */}
              <FormGroup title="Heading">
              {/* Email Display Heading — the visible heading rendered
                  inside the email body, distinct from the mailbox
                  subject line above. Same HeadingStyleControls used
                  by every Section Header below so a template author
                  and a franchisee share exactly one typography UX. */}
              <Field label="Email display heading">
                <HeadingStyleControls
                  value={displayHeadingStyle}
                  onChange={setDisplayHeadingStyle}
                  defaults={DEFAULT_DISPLAY_HEADING_STYLE}
                  testid="marketing-display-heading-style"
                />
                <input
                  value={displayHeading}
                  onChange={(e) => setDisplayHeading(e.target.value)}
                  data-testid="marketing-display-heading"
                  maxLength={200}
                  className="mt-2 w-full px-3 py-2.5 border border-stone-300 rounded-xl text-sm focus:outline-none focus:border-stone-950"
                  placeholder="e.g. Summer craft sessions are back!"
                />
                <div className="mt-1 text-[11px] text-stone-500 italic">
                  Optional — leave blank to skip. Shown below the header image.
                </div>
              </Field>

              {/* Top-level Intro — sits above every section in the
                  email. Personalised once via {{first_name}}. */}
              <Field label="Intro text" required>
                <HeadingStyleControls
                  value={introStyle}
                  onChange={setIntroStyle}
                  defaults={DEFAULT_BODY_TEXT_STYLE}
                  editorRef={introEditorRef}
                  showLink
                  showInsertName
                  testid="marketing-top-intro-style"
                />
                <MarketingIntroEditor
                  ref={introEditorRef}
                  value={introHtml}
                  onChange={setIntroHtml}
                  placeholder="Hi {{first_name}}, hope you're well…"
                  blockStyle={introStyle}
                  testid="marketing-top-intro"
                />
              </Field>
              </FormGroup>

              {/* ============================================================
                   BACKGROUND COLOUR
                   ============================================================ */}
              <FormGroup title="Background colour">
              {/* Whole-mailer background colour — full picker + hex input. */}
              <Field label="Background colour">
                <div className="flex items-center gap-2" data-testid="marketing-bg-color">
                  <input
                    type="color"
                    value={backgroundColor}
                    onChange={(e) => setBackgroundColor(e.target.value)}
                    className="h-10 w-12 rounded-lg border border-stone-300 cursor-pointer p-0"
                    data-testid="marketing-bg-color-picker"
                    title="Pick background colour"
                  />
                  <input
                    type="text"
                    value={backgroundColor}
                    onChange={(e) => setBackgroundColor(e.target.value)}
                    placeholder="#f7f7f4"
                    className="flex-1 px-3 py-2.5 border border-stone-300 rounded-xl text-sm font-mono focus:outline-none focus:border-stone-950"
                    data-testid="marketing-bg-color-input"
                  />
                  <button
                    type="button"
                    onClick={() => setBackgroundColor(DEFAULT_BG)}
                    className="px-3 py-2 text-[10px] font-bold uppercase tracking-wider border border-stone-300 hover:bg-stone-50 rounded-lg text-stone-700"
                    data-testid="marketing-bg-color-reset"
                    title="Reset to default cream"
                  >Reset</button>
                </div>
              </Field>
              {/* Body background colour — a second, optional picker
                  that paints the inner content card BELOW the header
                  graphic. Empty = keep the historical white so
                  existing campaigns/templates render unchanged. */}
              <Field label="Body background colour">
                <div className="flex items-center gap-2" data-testid="marketing-body-bg-color">
                  <input
                    type="color"
                    value={bodyBackgroundColor || "#ffffff"}
                    onChange={(e) => setBodyBackgroundColor(e.target.value)}
                    className="h-10 w-12 rounded-lg border border-stone-300 cursor-pointer p-0"
                    data-testid="marketing-body-bg-color-picker"
                    title="Pick body background colour"
                  />
                  <input
                    type="text"
                    value={bodyBackgroundColor}
                    onChange={(e) => setBodyBackgroundColor(e.target.value)}
                    placeholder="Leave blank for white"
                    className="flex-1 px-3 py-2.5 border border-stone-300 rounded-xl text-sm font-mono focus:outline-none focus:border-stone-950"
                    data-testid="marketing-body-bg-color-input"
                  />
                  <button
                    type="button"
                    onClick={() => setBodyBackgroundColor("")}
                    className="px-3 py-2 text-[10px] font-bold uppercase tracking-wider border border-stone-300 hover:bg-stone-50 rounded-lg text-stone-700"
                    data-testid="marketing-body-bg-color-reset"
                    title="Clear — inner card falls back to white"
                  >Clear</button>
                </div>
                <div className="mt-1 text-[11px] text-stone-500 italic">
                  Optional — colours the email body area beneath the header graphic. Leave blank to keep the classic white card.
                </div>
              </Field>
              </FormGroup>

              {/* ============================================================
                   EMAIL HEADER GRAPHIC
                   ============================================================ */}
              <FormGroup title="Email header graphic">
              {/* Marketing+ v2 — Header selector. Franchisee picks
                  the standard Mojo logo or uploads a custom banner
                  for THIS e-shot only (Christmas, Easter etc). */}
              <Field label="Email header">
                <div className="grid grid-cols-2 gap-2" data-testid="marketing-header-type">
                  <button
                    type="button"
                    onClick={() => setHeaderType(HEADER_STANDARD)}
                    data-testid="marketing-header-type-standard"
                    className={`px-3 py-2 rounded-lg border text-[11px] font-bold uppercase tracking-wider ${headerType === HEADER_STANDARD
                      ? "border-stone-950 bg-stone-950 text-white"
                      : "border-stone-300 hover:bg-stone-50 text-stone-700"}`}
                  >Standard Mojo header</button>
                  <button
                    type="button"
                    onClick={() => setHeaderType(HEADER_CUSTOM)}
                    data-testid="marketing-header-type-custom"
                    className={`px-3 py-2 rounded-lg border text-[11px] font-bold uppercase tracking-wider ${headerType === HEADER_CUSTOM
                      ? "border-stone-950 bg-stone-950 text-white"
                      : "border-stone-300 hover:bg-stone-50 text-stone-700"}`}
                  >Custom header image</button>
                </div>
                {headerType === HEADER_CUSTOM && (
                  <div className="mt-3" data-testid="marketing-custom-header">
                    {customHeaderImageUrl ? (
                      <div className="border border-stone-300 rounded-xl overflow-hidden bg-stone-100 flex items-center justify-between gap-2 p-2">
                        <img
                          src={customHeaderImageUrl}
                          alt="Custom header"
                          className="block max-h-[80px] w-auto object-contain"
                          data-testid="marketing-custom-header-preview"
                        />
                        <button
                          type="button"
                          onClick={() => { setCustomHeaderImageUrl(""); setCustomHeaderImageKey(""); }}
                          data-testid="marketing-custom-header-remove"
                          className="text-[10px] font-bold uppercase tracking-wider text-stone-500 hover:text-red-600"
                        >Remove</button>
                      </div>
                    ) : (
                      <label
                        className="flex flex-col items-center justify-center px-4 py-5 border-2 border-dashed border-stone-300 rounded-xl cursor-pointer hover:bg-stone-50 text-stone-500"
                        data-testid="marketing-custom-header-upload"
                      >
                        {customHeaderUploading ? (
                          <Loader2 className="w-5 h-5 animate-spin text-stone-400" />
                        ) : (
                          <>
                            <ImageIcon className="w-5 h-5 mb-1 text-stone-400" />
                            <span className="text-sm font-medium">Upload a custom header image</span>
                            <span className="text-[11px] mt-0.5">Best 600×320 · scales responsively · max 12 MB</span>
                          </>
                        )}
                        <input
                          type="file"
                          accept="image/*"
                          className="hidden"
                          onChange={async (e) => {
                            const f = e.target.files?.[0];
                            if (!f) return;
                            e.target.value = "";
                            setCustomHeaderUploading(true); setError("");
                            try {
                              const fd = new FormData();
                              fd.append("file", f);
                              const { data } = await api.post(uploadEndpoint, fd, {
                                headers: { "Content-Type": "multipart/form-data" },
                              });
                              setCustomHeaderImageUrl(data.image_url);
                              setCustomHeaderImageKey(data.key);
                            } catch (ex) {
                              setError(ex?.response?.data?.detail || "Header upload failed");
                            } finally { setCustomHeaderUploading(false); }
                          }}
                        />
                      </label>
                    )}
                    <div className="mt-1.5 text-[11px] text-stone-500 italic">
                      Replaces the Mojo logo for this campaign only. Preserves aspect ratio and caps at 600×320.
                    </div>
                  </div>
                )}
              </Field>
              </FormGroup>

              {/* ============================================================
                   SECTIONS
                   ============================================================ */}
              <FormGroup title="Sections">
              {/* Repeatable content sections — each visibly separated
                  by a top border + generous spacing so one section
                  never spills into the next. */}
              <div className="space-y-8">
                {panels.map((panel, idx) => (
                  <PanelEditor
                    key={idx}
                    idx={idx}
                    panel={panel}
                    introStyle={introStyle}
                    canRemove={panels.length > 1}
                    onChange={(patch) => updatePanel(idx, patch)}
                    onRemove={() => removePanel(idx)}
                    onPickImage={(e) => onPickImage(idx, e)}
                    onOpenPool={() => setPoolPickerIdx(idx)}
                    onClearImage={() => updatePanel(idx, { image_url: "", image_key: "" })}
                    uploading={uploading}
                  />
                ))}
                {panels.length < MAX_PANELS && (
                  <button
                    type="button"
                    onClick={addPanel}
                    data-testid="marketing-add-section"
                    className="w-full inline-flex items-center justify-center gap-2 px-3 py-2 text-xs font-bold uppercase tracking-wider border-2 border-dashed border-stone-300 text-stone-600 hover:bg-stone-50 rounded-xl"
                  >
                    <Plus className="w-3.5 h-3.5" /> Add another section
                  </button>
                )}
              </div>
              </FormGroup>

              {/* Bookings link — HIDDEN in the composer for both HQ
                  template mode and franchisee campaign mode while
                  Bookings+ is not available to franchisees in
                  production. Backend + state + payload wiring
                  intentionally left in place so we can re-enable the
                  control later without touching data (existing
                  campaigns that already stored
                  `include_bookings_link: true` continue to render
                  their button on send — we just don't expose the UI
                  to toggle it). Do not remove the state hook. */}

              {/* Footer contact block — campaign mode only. Templates
                  are content-only; each franchisee's own footer
                  settings apply at send-time when they copy this
                  template into their own campaign. */}
              {!isTemplate && (
              <div className="border border-stone-200 rounded-xl bg-stone-50/60 p-3" data-testid="marketing-footer-block">
                <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-stone-600 mb-2 flex items-center justify-between gap-3">
                  <span>Footer · what to include on this e-shot</span>
                  <a
                    href="/portal/marketing/settings"
                    className="text-[10px] font-bold text-stone-700 hover:text-stone-950 underline normal-case tracking-normal"
                  >Edit my marketing settings →</a>
                </div>
                <div className="space-y-1.5">
                  <label className={`flex items-center gap-2 text-sm ${access?.phone ? "text-stone-800 cursor-pointer" : "text-stone-400 cursor-not-allowed"}`}>
                    <input
                      type="checkbox"
                      checked={footerShowPhone && !!access?.phone}
                      disabled={!access?.phone}
                      onChange={(e) => setFooterShowPhone(e.target.checked)}
                      data-testid="footer-show-phone"
                      className="w-4 h-4 rounded border-stone-300 text-stone-900"
                    />
                    Phone — {access?.phone || <em className="text-stone-400">no phone on your profile</em>}
                  </label>
                  <label className={`flex items-center gap-2 text-sm ${access?.from_email ? "text-stone-800 cursor-pointer" : "text-stone-400 cursor-not-allowed"}`}>
                    <input
                      type="checkbox"
                      checked={footerShowEmail && !!access?.from_email}
                      disabled={!access?.from_email}
                      onChange={(e) => setFooterShowEmail(e.target.checked)}
                      data-testid="footer-show-email"
                      className="w-4 h-4 rounded border-stone-300 text-stone-900"
                    />
                    Email — {access?.from_email || <em className="text-stone-400">no email</em>}
                  </label>
                  <label className={`flex items-center gap-2 text-sm ${access?.facebook_url ? "text-stone-800 cursor-pointer" : "text-stone-400 cursor-not-allowed"}`}>
                    <input
                      type="checkbox"
                      checked={footerShowFacebook && !!access?.facebook_url}
                      disabled={!access?.facebook_url}
                      onChange={(e) => setFooterShowFacebook(e.target.checked)}
                      data-testid="footer-show-facebook"
                      className="w-4 h-4 rounded border-stone-300 text-stone-900"
                    />
                    Facebook page — {access?.facebook_url
                      ? <span className="truncate max-w-[260px] inline-block align-bottom" title={access.facebook_url}>{access.facebook_url}</span>
                      : <em className="text-stone-400">add a link in marketing settings</em>}
                  </label>
                  <label className={`flex items-center gap-2 text-sm ${access?.instagram_url ? "text-stone-800 cursor-pointer" : "text-stone-400 cursor-not-allowed"}`}>
                    <input
                      type="checkbox"
                      checked={footerShowInstagram && !!access?.instagram_url}
                      disabled={!access?.instagram_url}
                      onChange={(e) => setFooterShowInstagram(e.target.checked)}
                      data-testid="footer-show-instagram"
                      className="w-4 h-4 rounded border-stone-300 text-stone-900"
                    />
                    Instagram — {access?.instagram_url
                      ? <span className="truncate max-w-[260px] inline-block align-bottom" title={access.instagram_url}>{access.instagram_url}</span>
                      : <em className="text-stone-400">add a link in marketing settings</em>}
                  </label>
                  <label className={`flex items-center gap-2 text-sm ${access?.custom_link_url ? "text-stone-800 cursor-pointer" : "text-stone-400 cursor-not-allowed"}`}>
                    <input
                      type="checkbox"
                      checked={footerShowCustom && !!access?.custom_link_url}
                      disabled={!access?.custom_link_url}
                      onChange={(e) => setFooterShowCustom(e.target.checked)}
                      data-testid="footer-show-custom"
                      className="w-4 h-4 rounded border-stone-300 text-stone-900"
                    />
                    {access?.custom_link_label || "Custom link"} — {access?.custom_link_url
                      ? <span className="truncate max-w-[260px] inline-block align-bottom" title={access.custom_link_url}>{access.custom_link_url}</span>
                      : <em className="text-stone-400">add a link in marketing settings</em>}
                  </label>
                </div>
                <div className="mt-2 text-[11px] text-stone-500 italic">
                  The Creative Mojo logo at the top of each e-shot links to your <strong>{access?.logo_target === "facebook" ? "Facebook page" : "Mojo franchise page"}</strong>. Change in settings.
                </div>
              </div>
              )}

              {/* Recipients — campaign mode only. */}
              {!isTemplate && (
              <div>
                <label className="text-[10px] uppercase tracking-[0.2em] font-bold text-stone-600 mb-1.5 flex items-center justify-between">
                  <span>Recipients <span className="text-stone-400 normal-case font-normal">(max 5 per send)</span></span>
                  <span data-testid="marketing-recipients-count" className={`px-2 py-0.5 rounded ${selectedKeys.size === MAX_RECIPIENTS ? "bg-amber-100 text-amber-900" : "bg-stone-100 text-stone-700"}`}>
                    {selectedKeys.size} / {MAX_RECIPIENTS}
                  </span>
                </label>
                <div className="relative mb-2">
                  <Search className="w-3.5 h-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-stone-400" />
                  <input
                    value={recipientSearch}
                    onChange={(e) => setRecipientSearch(e.target.value)}
                    placeholder="Search clients & contacts…"
                    className="w-full pl-9 pr-3 py-2 text-sm border border-stone-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-stone-900/20"
                    data-testid="marketing-recipient-search"
                  />
                </div>
                <div className="max-h-72 overflow-y-auto border border-stone-200 rounded-xl divide-y divide-stone-100" data-testid="marketing-recipient-list">
                  {recipientsLoading ? (
                    <div className="flex items-center justify-center py-8 text-stone-400 text-sm">
                      <Loader2 className="w-4 h-4 animate-spin mr-2" /> Loading clients…
                    </div>
                  ) : filteredRecipients.length === 0 ? (
                    <div className="px-3 py-6 text-center text-xs text-stone-500">
                      {recipients.length === 0
                        ? "No client contacts with email addresses yet. Add one from My Territory+."
                        : "No matches."}
                    </div>
                  ) : filteredRecipients.map((r) => {
                    const key = `${r.client_id}:${r.contact_index}`;
                    const checked = selectedKeys.has(key);
                    return (
                      <button
                        key={key}
                        type="button"
                        onClick={() => toggleRecipient(r)}
                        data-testid={`marketing-recipient-${r.client_id}-${r.contact_index}`}
                        className={`w-full px-3 py-2 text-left flex items-center gap-3 hover:bg-stone-50 ${checked ? "bg-emerald-50" : ""}`}
                      >
                        <input type="checkbox" readOnly checked={checked}
                          className="w-4 h-4 rounded border-stone-300 text-stone-900 focus:ring-stone-900 pointer-events-none" />
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-medium text-stone-900 truncate">
                            {r.name} <span className="text-stone-400 font-normal">· {r.role}</span>
                          </div>
                          <div className="text-xs text-stone-500 truncate">
                            {r.email}{r.organisation ? ` — ${r.organisation}` : ""}
                          </div>
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>
              )}
            </div>

            {/* RIGHT — preview. Sticky on desktop so it stays visible
                while the user scrolls the editor on the left. */}
            <div className="bg-stone-50 px-6 py-5 flex flex-col gap-3 lg:sticky lg:top-0 lg:self-start lg:max-h-[calc(95vh-160px)] lg:overflow-y-auto">
              <div className="text-[10px] uppercase tracking-[0.3em] font-bold text-stone-700 flex items-center gap-1.5 sticky top-0 bg-stone-50 -mx-6 px-6 py-1 z-10">
                <EyeIcon /> Live Preview
                <span className="text-stone-400 font-normal normal-case tracking-normal ml-auto">Rendered as recipients will see it</span>
              </div>
              <div className="bg-white border border-stone-200 rounded-xl overflow-hidden shrink-0">
                {previewHtml ? (
                  <AutosizeIframe
                    srcDoc={previewHtml}
                    testid="marketing-preview-iframe"
                  />
                ) : (
                  <div className="flex items-center justify-center h-[420px] text-stone-400 text-sm">
                    <Loader2 className="w-4 h-4 animate-spin mr-2" /> Rendering…
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-3 border-t border-stone-200 bg-stone-50 flex items-center justify-between gap-3 flex-wrap shrink-0">
          <div className="flex-1 min-w-0">
            {error && (
              <div className="text-xs text-red-700 flex items-center gap-1.5" data-testid="marketing-error">
                <AlertCircle className="w-3.5 h-3.5" /> {error}
              </div>
            )}
            {info && !error && (
              <div className="text-xs text-emerald-700 flex items-center gap-1.5" data-testid="marketing-info">
                <CheckCircle2 className="w-3.5 h-3.5" /> {info}
              </div>
            )}
          </div>
          {isTemplate && (
            <>
              <span
                className={`text-[10px] uppercase tracking-wider font-bold px-2 py-1 rounded ${
                  templateStatus === "published"
                    ? "bg-emerald-100 text-emerald-800"
                    : templateStatus === "archived"
                    ? "bg-stone-200 text-stone-500"
                    : "bg-stone-100 text-stone-700"
                }`}
                data-testid="template-status-pill"
              >{templateStatus}</span>
              <button
                type="button"
                onClick={() => onClose?.()}
                data-testid="template-cancel"
                className="px-4 py-2 text-xs font-bold uppercase tracking-wider border border-stone-300 hover:bg-stone-50 text-stone-700 rounded-lg"
              >Cancel</button>
              <button
                type="button"
                onClick={saveTemplate}
                disabled={templateSaving || !templateName.trim()}
                data-testid="template-save"
                className="px-4 py-2 text-xs font-bold uppercase tracking-wider bg-stone-950 hover:bg-stone-800 text-white rounded-lg flex items-center gap-1.5 disabled:opacity-50"
              >
                {templateSaving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <CheckCircle2 className="w-3.5 h-3.5" />}
                Save
              </button>
              {templateStatus !== "published" && (
                <button
                  type="button"
                  onClick={() => templateStatusChange("published")}
                  disabled={templateSaving || !templateName.trim()}
                  data-testid="template-publish"
                  className="px-4 py-2 text-xs font-bold uppercase tracking-wider bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg flex items-center gap-1.5 disabled:opacity-50"
                >
                  <Send className="w-3.5 h-3.5" /> Publish
                </button>
              )}
              {templateStatus === "published" && (
                <button
                  type="button"
                  onClick={() => templateStatusChange("draft")}
                  disabled={templateSaving}
                  data-testid="template-unpublish"
                  className="px-4 py-2 text-xs font-bold uppercase tracking-wider border border-stone-300 hover:bg-stone-50 text-stone-700 rounded-lg flex items-center gap-1.5 disabled:opacity-50"
                >
                  <RefreshCw className="w-3.5 h-3.5" /> Unpublish
                </button>
              )}
              {templateStatus !== "archived" && templateId && (
                <button
                  type="button"
                  onClick={() => templateStatusChange("archived", "Archive this template? It will disappear from the franchisee picker.")}
                  disabled={templateSaving}
                  data-testid="template-archive"
                  className="px-4 py-2 text-xs font-bold uppercase tracking-wider text-stone-500 hover:text-red-600 rounded-lg flex items-center gap-1.5 disabled:opacity-50"
                >Archive</button>
              )}
            </>
          )}
          {!isTemplate && (<>
          <button
            type="button"
            onClick={saveDraft}
            disabled={!canSaveDraft}
            data-testid="marketing-save-draft"
            className="px-4 py-2 text-xs font-bold uppercase tracking-wider border border-stone-300 hover:bg-stone-50 text-stone-700 rounded-lg flex items-center gap-1.5 disabled:opacity-50"
          >
            {savingDraft ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <FileText className="w-3.5 h-3.5" />}
            Save draft
          </button>
          <button
            type="button"
            onClick={handleClose}
            data-testid="marketing-done-btn"
            className="px-4 py-2 text-xs font-bold uppercase tracking-wider border border-stone-900 hover:bg-stone-900 hover:text-white text-stone-900 rounded-lg flex items-center gap-1.5"
            title="Save & close — drafts are kept automatically"
          >
            <CheckCircle2 className="w-3.5 h-3.5" /> Done
          </button>
          <button
            type="button"
            onClick={sendTest}
            disabled={testing || !title.trim() || !hasAnyContent}
            data-testid="marketing-test-send"
            className="px-4 py-2 text-xs font-bold uppercase tracking-wider border border-stone-300 hover:bg-stone-50 text-stone-700 rounded-lg flex items-center gap-1.5 disabled:opacity-50"
          >
            {testing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
            Send test to me
          </button>
          <button
            type="button"
            onClick={send}
            disabled={!canSend}
            data-testid="marketing-send-btn"
            className="px-5 py-2 text-xs font-bold uppercase tracking-wider bg-[#dddd16] hover:bg-[#aaaa11] text-stone-950 rounded-lg flex items-center gap-1.5 disabled:opacity-50"
          >
            {sending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
            Send to {selectedKeys.size || 0} recipient{selectedKeys.size === 1 ? "" : "s"}
          </button>
          </>)}
        </div>
      </div>

      <MarketingImageCropper
        open={cropOpen}
        file={rawImageFile}
        onCancel={() => setCropOpen(false)}
        onDone={onCropDone}
      />

      <ProjectPoolPickerModal
        open={poolPickerIdx !== null}
        onClose={() => setPoolPickerIdx(null)}
        onSelect={({ image_url, image_key }) => {
          if (poolPickerIdx == null) return;
          // Match the same shape ``onCropDone`` writes to the panel
          // so live preview, drafts, publishes and delivered emails
          // reuse the existing section-image plumbing unchanged.
          updatePanel(poolPickerIdx, { image_url, image_key });
        }}
      />
    </div>
  );
}

function PanelEditor({ idx, panel, canRemove, onChange, onRemove, onPickImage, onOpenPool, onClearImage, uploading, introStyle }) {
  // One editor ref per rich-text field — bridges the single-toolbar
  // HeadingStyleControls row to the section header + section body
  // contentEditable for Link / Insert Name actions.
  const headerEditorRef = useRef(null);
  const textEditorRef = useRef(null);
  return (
    <div className="border-t-2 border-stone-200 pt-6 first:border-t-0 first:pt-0" data-testid={`marketing-panel-${idx}`}>
      <div className="flex items-center justify-between mb-4">
        <div className="text-[11px] uppercase tracking-[0.3em] font-black text-stone-900">
          Section {idx + 1}
        </div>
        {canRemove && (
          <button
            type="button"
            onClick={onRemove}
            data-testid={`marketing-panel-remove-${idx}`}
            className="text-stone-400 hover:text-red-600 inline-flex items-center gap-1 text-[10px] uppercase tracking-wider font-bold"
          >
            <Trash2 className="w-3 h-3" /> Remove
          </button>
        )}
      </div>

      {/* Section header — optional heading rendered as a big bold
          dark heading above the rest of the section. Uses the same
          shared rich-text editor as Intro Text / Section Text so
          authors can bold/italic/underline/colour/align/link inline
          within the heading. The outer HeadingStyleControls still
          drive the section-wide typography (family/size/weight). */}
      <Field label="Section header (optional)">
        <HeadingStyleControls
          value={panel.header_style || DEFAULT_SECTION_HEADER_STYLE}
          onChange={(next) => onChange({ header_style: next })}
          defaults={DEFAULT_SECTION_HEADER_STYLE}
          editorRef={headerEditorRef}
          showLink
          testid={`marketing-header-style-${idx}`}
        />
        <MarketingIntroEditor
          ref={headerEditorRef}
          value={panel.header || ""}
          onChange={(html) => onChange({ header: html })}
          placeholder="e.g. Summer drop-in sessions"
          singleLine
          blockStyle={panel.header_style || DEFAULT_SECTION_HEADER_STYLE}
          testid={`marketing-header-${idx}`}
        />
      </Field>

      {/* Image */}
      <div className="mt-4">
        <label className="text-[10px] uppercase tracking-[0.2em] font-bold text-stone-600 mb-1.5 flex items-center justify-between">
          <span><ImageIcon className="w-3 h-3 inline mr-1" /> Image (optional)</span>
          {panel.image_url && (
            <button
              onClick={onClearImage}
              data-testid={`marketing-image-remove-${idx}`}
              className="text-stone-400 hover:text-red-600 text-[10px] inline-flex items-center gap-1"
            >
              <Trash2 className="w-3 h-3" /> Remove
            </button>
          )}
        </label>
        {panel.image_url ? (
          <div className="border border-stone-300 rounded-xl overflow-hidden bg-stone-100 inline-flex items-start max-w-[200px]">
            <img
              src={panel.image_url}
              alt=""
              className="block w-auto max-h-[200px] object-contain"
              data-testid={`marketing-image-preview-${idx}`}
            />
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2" data-testid={`marketing-image-choices-${idx}`}>
            <label
              className="flex flex-col items-center justify-center px-4 py-6 border-2 border-dashed border-stone-300 rounded-xl cursor-pointer hover:bg-stone-50 text-stone-500"
              data-testid={`marketing-image-upload-${idx}`}
            >
              {uploading ? (
                <Loader2 className="w-5 h-5 animate-spin text-stone-400" />
              ) : (
                <>
                  <ImageIcon className="w-6 h-6 mb-1.5 text-stone-400" />
                  <span className="text-sm font-medium">Upload &amp; crop an image</span>
                  <span className="text-[11px] mt-0.5">Free-form crop · max 12 MB · JPG / PNG / WebP</span>
                </>
              )}
              <input type="file" accept="image/*" onChange={onPickImage} className="hidden" />
            </label>
            <button
              type="button"
              onClick={onOpenPool}
              data-testid={`marketing-image-pool-${idx}`}
              className="flex flex-col items-center justify-center px-4 py-6 border-2 border-dashed border-stone-300 rounded-xl cursor-pointer hover:bg-stone-50 text-stone-500"
            >
              <ImageIcon className="w-6 h-6 mb-1.5 text-stone-400" />
              <span className="text-sm font-bold uppercase tracking-wider text-stone-700">Add image from Mojo Project Pool</span>
              <span className="text-[11px] mt-0.5 normal-case tracking-normal font-normal">Head Office curated · no cropping needed</span>
            </button>
          </div>
        )}
      </div>

      {/* Layout selector — only relevant when an image is present.
          Drives where the section TEXT sits relative to the image. */}
      {panel.image_url && (
        <div className="mt-3">
          <label className="text-[10px] uppercase tracking-[0.2em] font-bold text-stone-600 mb-1.5 block">
            Where should the text go?
          </label>
          <div className="grid grid-cols-3 gap-2" data-testid={`marketing-layout-${idx}`}>
            {[
              { id: "image-top",   label: "Text below",  svg: <LayoutTopIcon /> },
              { id: "image-left",  label: "Text right",  svg: <LayoutLeftIcon /> },
              { id: "image-right", label: "Text left",   svg: <LayoutRightIcon /> },
            ].map((opt) => {
              const active = (panel.layout || "image-top") === opt.id;
              return (
                <button
                  key={opt.id}
                  type="button"
                  onClick={() => onChange({ layout: opt.id })}
                  data-testid={`marketing-layout-${idx}-${opt.id}`}
                  className={`flex flex-col items-center justify-center gap-1 px-2 py-2 rounded-lg border text-[10px] font-bold uppercase tracking-wider transition ${active
                    ? "border-stone-950 bg-stone-950 text-white"
                    : "border-stone-300 hover:bg-stone-50 text-stone-700"}`}
                >
                  <span className={active ? "opacity-100" : "opacity-70"}>{opt.svg}</span>
                  {opt.label}
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* Section body text — full RTE. Sits L/R/below the image
          according to the layout selector. */}
      <div className="mt-3">
        <Field label="Section text">
          <HeadingStyleControls
            value={panel.text_style || introStyle || DEFAULT_BODY_TEXT_STYLE}
            onChange={(next) => onChange({ text_style: next })}
            defaults={DEFAULT_BODY_TEXT_STYLE}
            hideReset
            editorRef={textEditorRef}
            showLink
            showInsertName
            testid={`marketing-text-style-${idx}`}
          />
          <MarketingIntroEditor
            ref={textEditorRef}
            value={panel.text_html || ""}
            onChange={(html) => onChange({ text_html: html })}
            placeholder="Tell people what this section is about…"
            blockStyle={panel.text_style || introStyle || DEFAULT_BODY_TEXT_STYLE}
            testid={`marketing-text-${idx}`}
          />
          {/* Inheritance affordance — visible only when this section
              has its OWN typography override. Clicking clears the
              override so the section starts inheriting the
              campaign-level intro_style again. When no override
              exists, the section is already inheriting the intro
              style and this pill would be redundant. */}
          {panel.text_style ? (
            <div className="mt-1 flex items-center gap-2 text-[10px]">
              <span className="uppercase tracking-wider font-bold text-amber-600">Custom section style</span>
              <button
                type="button"
                onClick={() => onChange({ text_style: null })}
                data-testid={`marketing-text-style-${idx}-inherit`}
                title="Clear this section's override and inherit the campaign-level Intro Text styling"
                className="px-2 h-6 uppercase tracking-wider font-bold text-stone-500 hover:text-stone-950 hover:bg-stone-100 rounded"
              >Use body default</button>
            </div>
          ) : (
            <div className="mt-1 text-[10px] uppercase tracking-wider font-bold text-stone-400">
              Inheriting Intro Text style
            </div>
          )}
        </Field>
      </div>

      {/* Button / call-to-action — franchisee picks one of two modes:
          a URL button (any external link) or an "Email me" button
          (mailto: link with a pre-filled subject). The actual email
          address comes from the franchisee's profile on the backend,
          so the franchisee never has to type it in here. */}
      <div className="mt-3">
        <label className="text-[10px] uppercase tracking-[0.2em] font-bold text-stone-600 mb-1.5 block">
          Call-to-action button (optional)
        </label>
        <div className="grid grid-cols-2 gap-2" data-testid={`marketing-link-type-${idx}`}>
          {[
            { id: "url",   label: "URL button",  icon: LinkIcon, hint: "Sends people to a webpage" },
            { id: "email", label: "Email me",     icon: Mail,    hint: "Opens their email app to message you" },
          ].map((opt) => {
            const Icon = opt.icon;
            const active = (panel.link_type || "url") === opt.id;
            return (
              <button
                key={opt.id}
                type="button"
                onClick={() => onChange({ link_type: opt.id })}
                data-testid={`marketing-link-type-${idx}-${opt.id}`}
                title={opt.hint}
                className={`flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg border text-[10px] font-bold uppercase tracking-wider transition ${active
                  ? "border-stone-950 bg-stone-950 text-white"
                  : "border-stone-300 hover:bg-stone-50 text-stone-700"}`}
              >
                <Icon className="w-3 h-3" /> {opt.label}
              </button>
            );
          })}
        </div>
      </div>

      {/* Mode-specific fields. */}
      {((panel.link_type || "url") === "url") ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-3">
          <Field label="Link URL (optional)">
            <div className="relative">
              <LinkIcon className="w-3.5 h-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-stone-400" />
              <input
                type="url"
                value={panel.link_url}
                onChange={(e) => onChange({ link_url: e.target.value })}
                data-testid={`marketing-link-url-${idx}`}
                placeholder="https://…"
                className="w-full pl-9 pr-3 py-2.5 border border-stone-300 rounded-xl text-sm focus:outline-none focus:border-stone-950"
              />
            </div>
          </Field>
          <Field label="Button label">
            <input
              value={panel.link_label}
              onChange={(e) => onChange({ link_label: e.target.value })}
              data-testid={`marketing-link-label-${idx}`}
              className="w-full px-3 py-2.5 border border-stone-300 rounded-xl text-sm focus:outline-none focus:border-stone-950"
            />
          </Field>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-3">
          <Field label="Email subject (recipient will see this)">
            <input
              value={panel.email_subject || ""}
              onChange={(e) => onChange({ email_subject: e.target.value })}
              data-testid={`marketing-email-subject-${idx}`}
              placeholder="I'd like more information please"
              className="w-full px-3 py-2.5 border border-stone-300 rounded-xl text-sm focus:outline-none focus:border-stone-950"
              maxLength={200}
            />
          </Field>
          <Field label="Button label">
            <input
              value={panel.link_label || "Email me"}
              onChange={(e) => onChange({ link_label: e.target.value })}
              data-testid={`marketing-link-label-${idx}`}
              className="w-full px-3 py-2.5 border border-stone-300 rounded-xl text-sm focus:outline-none focus:border-stone-950"
            />
          </Field>
          <div className="sm:col-span-2 text-[11px] text-stone-500 flex items-center gap-1.5">
            <Mail className="w-3 h-3" />
            The button will open the recipient&rsquo;s email app addressed to your franchisee email (set on your <a href="/portal/details" className="underline">My Franchise</a> page).
          </div>
        </div>
      )}
    </div>
  );
}

function Field({ label, required, children }) {
  return (
    <div>
      <label className="text-[10px] uppercase tracking-[0.2em] font-bold text-stone-600 mb-1.5 block">
        {label}{required && <span className="text-red-500 ml-0.5">*</span>}
      </label>
      {children}
    </div>
  );
}

// Clear visual grouping for the editor form. Renders a bold uppercase
// section heading with a horizontal divider so five main areas (Info,
// Heading, Background, Header Graphic, Sections) are visually distinct
// rather than running into one another. Deliberately light-touch — no
// cards, shadows or coloured boxes.
function FormGroup({ title, children }) {
  return (
    <section className="space-y-5" data-testid={`marketing-group-${title.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '')}`}>
      <div className="flex items-center gap-3">
        <h3 className="text-[11px] uppercase tracking-[0.3em] font-black text-stone-900 shrink-0">{title}</h3>
        <div className="flex-1 h-px bg-stone-300" />
      </div>
      <div className="space-y-5">
        {children}
      </div>
    </section>
  );
}

// Shared, compact typography controls used by BOTH the top-level
// Email Display Heading and every Section Header inside a panel.
// Kept deliberately lean — one horizontal row of family / size /
// colour / B / I / U / align — so the composer never sprouts a
// separate style system for section headings.
function HeadingStyleControls({
  value, onChange, defaults, testid, hideReset, onReset, resetLabel,
  // Optional imperative bridge to a sibling MarketingIntroEditor so
  // the toolbar can drive the editor's selection (insert link, insert
  // {{first_name}}). When ``editorRef`` is passed alongside the
  // corresponding ``showLink`` / ``showInsertName`` flag, this
  // toolbar becomes the single one-and-only formatting bar for that
  // text field — the editor itself has no toolbar.
  editorRef, showLink = false, showInsertName = false,
}) {
  const style = value || {};
  const set = (patch) => onChange({ ...style, ...patch });
  const resetStyle = () => {
    if (onReset) return onReset();
    onChange({ ...defaults });
  };
  const insertLink = () => {
    if (editorRef && editorRef.current && editorRef.current.insertLink) {
      editorRef.current.insertLink();
    }
  };
  const insertPlaceholder = () => {
    if (editorRef && editorRef.current && editorRef.current.insertPlaceholder) {
      editorRef.current.insertPlaceholder();
    }
  };
  const btnBase = "w-7 h-7 flex items-center justify-center rounded-lg border text-stone-700";
  const btnActive = "border-stone-950 bg-stone-950 text-white";
  const btnIdle = "border-stone-300 hover:bg-stone-50";
  const alignOpt = [
    { id: "left", Icon: AlignLeft, label: "Align left" },
    { id: "center", Icon: AlignCenter, label: "Align centre" },
    { id: "right", Icon: AlignRight, label: "Align right" },
  ];
  return (
    <div className="mt-2 flex flex-wrap items-center gap-1.5" data-testid={testid}>
      <select
        value={style.font_family || "Helvetica"}
        onChange={(e) => set({ font_family: e.target.value })}
        data-testid={`${testid}-family`}
        className="h-7 max-w-[110px] px-1.5 border border-stone-300 rounded-lg text-xs bg-white focus:outline-none focus:border-stone-950"
        style={{ fontFamily: (HEADING_FONTS.find((f) => f.name === (style.font_family || "Helvetica")) || {}).stack || undefined }}
      >
        {HEADING_FONTS.map((f) => (
          <option key={f.name} value={f.name} style={{ fontFamily: f.stack }}>{f.name}</option>
        ))}
      </select>
      <input
        type="number"
        min={10}
        max={72}
        value={style.font_size || 20}
        onChange={(e) => set({ font_size: parseInt(e.target.value || "0", 10) || 20 })}
        data-testid={`${testid}-size`}
        className="h-7 w-11 px-1.5 border border-stone-300 rounded-lg text-xs focus:outline-none focus:border-stone-950"
        title="Font size (px)"
      />
      <input
        type="color"
        value={style.color || "#1a1a1a"}
        onChange={(e) => set({ color: e.target.value })}
        data-testid={`${testid}-color`}
        className="h-7 w-8 p-0 border border-stone-300 rounded-lg cursor-pointer"
        title="Text colour"
      />
      <button
        type="button"
        onClick={() => {
          const nextBold = !style.bold;
          set({ bold: nextBold, font_weight: nextBold ? 700 : 400 });
        }}
        data-testid={`${testid}-bold`}
        className={`${btnBase} ${style.bold ? btnActive : btnIdle}`}
        title="Bold"
      ><Bold className="w-3.5 h-3.5" /></button>
      <button
        type="button"
        onClick={() => set({ italic: !style.italic })}
        data-testid={`${testid}-italic`}
        className={`${btnBase} ${style.italic ? btnActive : btnIdle}`}
        title="Italic"
      ><Italic className="w-3.5 h-3.5" /></button>
      <button
        type="button"
        onClick={() => set({ underline: !style.underline })}
        data-testid={`${testid}-underline`}
        className={`${btnBase} ${style.underline ? btnActive : btnIdle}`}
        title="Underline"
      ><UnderlineIcon className="w-3.5 h-3.5" /></button>
      <div className="flex items-center gap-0.5 ml-1">
        {alignOpt.map(({ id, Icon, label }) => {
          const active = (style.align || "left") === id;
          return (
            <button
              key={id}
              type="button"
              onClick={() => set({ align: id })}
              data-testid={`${testid}-align-${id}`}
              className={`${btnBase} ${active ? btnActive : btnIdle}`}
              title={label}
            ><Icon className="w-3.5 h-3.5" /></button>
          );
        })}
      </div>
      {showLink && (
        <button
          type="button"
          onClick={insertLink}
          data-testid={`${testid}-link`}
          title="Insert link — wraps the current selection in <a>. Only http(s):// and mailto: URLs allowed."
          className={`${btnBase} border-stone-300 hover:bg-stone-50 ml-1`}
        ><LinkIcon className="w-3.5 h-3.5" /></button>
      )}
      {showInsertName && (
        <button
          type="button"
          onClick={insertPlaceholder}
          data-testid={`${testid}-insert-name`}
          title="Insert {{first_name}} placeholder — replaced with the recipient's first name on send."
          className="ml-1 h-7 px-1.5 inline-flex items-center gap-1 rounded-lg border border-stone-300 hover:bg-stone-50 text-stone-700 text-[10px] font-bold uppercase tracking-wider"
        >
          <User className="w-3 h-3" /> Insert name
        </button>
      )}
      {defaults && !hideReset && (
        <button
          type="button"
          onClick={resetStyle}
          data-testid={`${testid}-reset`}
          title={resetLabel || "Reset typography to default"}
          className="ml-1 px-2 h-7 text-[10px] font-bold uppercase tracking-wider text-stone-500 hover:text-stone-950 hover:bg-stone-100 rounded-lg"
        >{resetLabel || "Reset"}</button>
      )}
    </div>
  );
}

function EyeIcon() {
  return (
    <svg className="w-3 h-3" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M2 12s3-7 10-7 10 7 10 7-3 7-10 7-10-7-10-7z" />
      <circle cx="12" cy="12" r="3" />
    </svg>
  );
}

// Self-sizing iframe — measures its content document's scrollHeight on
// load AND on every internal DOM/resize event, so the live preview
// pane grows with the rendered email instead of trapping the user in
// an inner scrollbar at a fixed 600px (which was clipping bottom
// sections like the franchisee signature/footer).
//
// The email design is a 600px content card centered in a wider canvas
// with 16px horizontal outer padding (see ``_build_html``'s outer
// ``<td style="padding:30px 16px">``). So the iframe body must be
// AT LEAST 600 + 16 + 16 = 632px wide to render without horizontal
// overflow. We use 640 for a 4px slack. Anything narrower and the
// iframe surfaces a horizontal scrollbar — which is exactly the bug
// we're fixing here.
//
// Note: srcDoc changes spawn a fresh document so the ResizeObserver
// has to be re-attached on every load — that's what onLoad is for.
const IFRAME_DESIGN_WIDTH = 640;
function AutosizeIframe({ srcDoc, testid }) {
  const ref = useRef(null);
  const wrapRef = useRef(null);
  // Start at 420px to avoid a 0-height flash before the first measure
  // resolves. Anything beyond that becomes content-driven.
  const [height, setHeight] = useState(420);
  // On narrow screens we scale the iframe down so the preview fits
  // edge-to-edge — admin can still see exactly what the recipient
  // sees, just smaller.
  const [scale, setScale] = useState(1);

  const computeScale = useCallback(() => {
    const wrap = wrapRef.current;
    if (!wrap) return;
    // Subtract a couple of pixels so the iframe never grazes the
    // rounded outer container and triggers a scrollbar — the goal is
    // to always show the full design edge-to-edge inside its
    // available space, never crop or overflow.
    const w = Math.max(0, (wrap.clientWidth || 0) - 2);
    if (w <= 0) return;
    const s = Math.min(1, w / IFRAME_DESIGN_WIDTH);
    setScale((prev) => (Math.abs(s - prev) > 0.005 ? s : prev));
  }, []);

  useEffect(() => {
    computeScale();
    const onResize = () => computeScale();
    window.addEventListener("resize", onResize);
    // Container width can change without a window resize (e.g. the
    // modal mounts, sidebar collapses, sticky pane recalculates).
    // ResizeObserver keeps the scale honest in all of those cases so
    // the preview never overflows its container width.
    let ro = null;
    if (wrapRef.current && typeof ResizeObserver !== "undefined") {
      ro = new ResizeObserver(() => computeScale());
      ro.observe(wrapRef.current);
    }
    return () => {
      window.removeEventListener("resize", onResize);
      if (ro) ro.disconnect();
    };
  }, [computeScale]);

  const measure = useCallback(() => {
    const ifr = ref.current;
    if (!ifr) return;
    try {
      const doc = ifr.contentDocument;
      if (!doc?.body) return;
      const h = Math.max(
        doc.body.scrollHeight,
        doc.documentElement?.scrollHeight || 0,
      );
      setHeight((prev) => (Math.abs(h - prev) > 4 ? h : prev));
    } catch {
      /* same-origin srcDoc — but be defensive against detached frames */
    }
  }, []);

  const onLoad = useCallback(() => {
    measure();
    const ifr = ref.current;
    if (!ifr) return;
    try {
      const doc = ifr.contentDocument;
      if (!doc) return;
      const imgs = Array.from(doc.images || []);
      imgs.forEach((img) => {
        if (!img.complete) {
          img.addEventListener("load", measure, { once: true });
          img.addEventListener("error", measure, { once: true });
        }
      });
      setTimeout(measure, 250);
    } catch { /* noop */ }
  }, [measure]);

  return (
    <div ref={wrapRef} className="w-full overflow-hidden" style={{ height: `${height * scale}px` }}>
      <iframe
        ref={ref}
        title="Email preview"
        srcDoc={srcDoc}
        onLoad={onLoad}
        scrolling="no"
        style={{
          width: `${IFRAME_DESIGN_WIDTH}px`,
          height: `${height}px`,
          transform: `scale(${scale})`,
          transformOrigin: "top left",
        }}
        className="border-0 block"
        data-testid={testid}
      />
    </div>
  );
}

// Tiny layout-preview icons used by the per-panel layout selector.
// Each one shows a 24×16 mock-up of the email panel with a shaded
// "image" block + "text lines" so the franchisee can pick by sight.
function LayoutTopIcon() {
  return (
    <svg width="32" height="22" viewBox="0 0 32 22" fill="none">
      <rect x="2" y="2" width="28" height="9" rx="1" fill="currentColor" opacity="0.6" />
      <rect x="2" y="13" width="28" height="1.5" rx="0.5" fill="currentColor" />
      <rect x="2" y="16" width="20" height="1.5" rx="0.5" fill="currentColor" />
      <rect x="2" y="19" width="24" height="1.5" rx="0.5" fill="currentColor" />
    </svg>
  );
}
function LayoutLeftIcon() {
  return (
    <svg width="32" height="22" viewBox="0 0 32 22" fill="none">
      <rect x="2" y="3" width="13" height="16" rx="1" fill="currentColor" opacity="0.6" />
      <rect x="17" y="4" width="13" height="1.5" rx="0.5" fill="currentColor" />
      <rect x="17" y="8" width="11" height="1.5" rx="0.5" fill="currentColor" />
      <rect x="17" y="12" width="12" height="1.5" rx="0.5" fill="currentColor" />
      <rect x="17" y="16" width="9" height="1.5" rx="0.5" fill="currentColor" />
    </svg>
  );
}
function LayoutRightIcon() {
  return (
    <svg width="32" height="22" viewBox="0 0 32 22" fill="none">
      <rect x="17" y="3" width="13" height="16" rx="1" fill="currentColor" opacity="0.6" />
      <rect x="2" y="4" width="13" height="1.5" rx="0.5" fill="currentColor" />
      <rect x="2" y="8" width="11" height="1.5" rx="0.5" fill="currentColor" />
      <rect x="2" y="12" width="12" height="1.5" rx="0.5" fill="currentColor" />
      <rect x="2" y="16" width="9" height="1.5" rx="0.5" fill="currentColor" />
    </svg>
  );
}
